import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import iconCircleClose from "../../../../img/icons/circle-close.svg";
import {
    IAddress,
    IBillingAddress,
    IShippingAddress,
} from "../../../models/address";
import { getAddressParts } from "../../../utils/format";
import { focusElement } from "../../../utils/keyboardFocus";
import { IReduxFormState } from "../reducers.interfaces";

import styles from "../AddressSuggestionModal/index.module.scss";

type IProps = Immutable<{
    form: IReduxFormState;
    onContinue: () => void;
    onEdit: () => void;
    showAddressWarningModal: boolean;
    addressPrefix: string;
}>;

export const AddressWarningModal = ({
    form,
    onContinue,
    onEdit,
    showAddressWarningModal,
    addressPrefix,
}: IProps): React.ReactNode => {
    const modalStyle: Modal.Styles = {
        content: {
            position: "absolute",
            top: "50%",
            bottom: "auto",
            left: "50%",
            right: "auto",
            transform: "translate(-50%, -50%)",
            width: "400px",
            padding: "30px",
            overflow: "auto",
        },
    };

    const formAddress: Partial<IAddress> = [
        "first_name",
        "last_name",
        "line1",
        "line2",
        "line4",
        "state",
        "postcode",
    ].reduce((address, field) => {
        const key = (addressPrefix + "_" + field) as keyof (
            | IShippingAddress
            | IBillingAddress
        );
        return { ...address, [field]: form[key] };
    }, {});

    const addressParts = getAddressParts({
        line1: formAddress.line1 || "",
        line2: formAddress.line2,
        city: formAddress.line4 || "",
        state: formAddress.state || "",
        postal: formAddress.postcode,
    });

    return (
        <Modal
            aria={{ modal: true }}
            contentLabel={t`Address Verification`}
            style={modalStyle}
            isOpen={showAddressWarningModal}
            onRequestClose={() => {
                onEdit();
            }}
            onAfterOpen={() => {
                focusElement(`.${styles.closeButton}`);
            }}
            role="dialog"
        >
            <button
                className={styles.closeButton}
                aria-label={t`close`}
                onClick={() => {
                    onEdit();
                }}
            >
                <SVG
                    aria-hidden="true"
                    className={styles.closeIcon}
                    src={iconCircleClose}
                    title={t`Close Icon`}
                />
            </button>
            <div className="address-warning-model">
                <h2 className={styles.header}>
                    {t`Please verify your address`}
                </h2>
                <p>
                    {richT(
                        t`Our system doesn't recognize this address.<br />Please confirm:`,
                    )}
                </p>
                <ul>
                    <li>{t`The house number is accurate`}</li>
                    <li>{t`There are no special characters`}</li>
                    <li>{t`The city name is not abbreviated`}</li>
                </ul>
                <p className="address-suggestion-model__address">
                    <span className={styles.addressLine}>
                        {formAddress.first_name} {formAddress.last_name}
                    </span>
                    <span className={styles.addressLine}>
                        {addressParts.streetLine1}
                    </span>
                    {addressParts.streetLine2 && (
                        <span className={styles.addressLine}>
                            {addressParts.streetLine2}
                        </span>
                    )}
                    <span className={styles.addressLine}>
                        {addressParts.cityStatePostal}
                    </span>
                </p>
                <button
                    className={`${styles.continueButton} button`}
                    onClick={onContinue}
                >
                    <span>{t`CONTINUE AS IS`}</span>
                </button>
                <button
                    className={`${styles.editButton} button button--secondary`}
                    onClick={onEdit}
                >
                    <span>{t`EDIT`}</span>
                </button>
            </div>
        </Modal>
    );
};
